import {useEffect} from 'react';
import {usePrevious} from '../../../../../../common/shared/hooks/usePrevious';
import {verticalWindowScrollIntoView} from '../../../../../../common/shared/utils/verticalWindowScrollIntoView';
import {FontLoadStatus} from '../type/FontLoadStatus.enum';
import {Fullscreen} from '../type/Fullscreen.interface';
import {ProductKey} from '../type/KeyType.type';
import {TalkDataType} from '../type/TalkDataType.enum';
import {TalkMap} from '../type/TalkMap.interface';
import {ViewingMode} from '../type/ViewingMode.enum';
import {scrollToWindowBottommost} from '../util/scrollToWindowBottommost';

interface Props {
  isViewerLoaded: boolean;
  isViewerEndExposed: boolean;
  fontLoadStatus: FontLoadStatus;
  fullscreen?: Fullscreen;
  viewingMode: ViewingMode;
  calculateAndDispatchExposedArea: Function;
  talkMap: TalkMap;
  currentProductId?: ProductKey;
  visitedTalkCount: number;
  talkElementMap: {[key: string]: HTMLDivElement};
}

export function useFocusTalk({
  isViewerEndExposed,
  isViewerLoaded,
  fontLoadStatus,
  fullscreen,
  viewingMode,
  calculateAndDispatchExposedArea,
  talkMap,
  talkElementMap,
  currentProductId,
  visitedTalkCount,
}: Props) {
  const prevFullscreen = usePrevious(fullscreen);
  useEffect(() => {
    if (
      !isViewerLoaded ||
      isViewerEndExposed ||
      // 웹폰트 로딩 후에는 텍스트들이 재배치되면서 컴포넌트의 크기가 변할 수 있다. 즉 스크롤이 정확하지 않은 위치로 갈 수 있어서,
      // 웹폰트 로딩 이전에는 스크롤을 작동시키지 않는다.
      fontLoadStatus === FontLoadStatus.BeforeLoad
    ) {
      return;
    }
    // 바로 이전에 풀스크린이었다가 현재는 아닐 때, 즉 풀스크린이 닫힌 직후에 스크롤 처리를 하기 위한 로직
    // 풀스크린이 닫힌 뒤에 스크롤이 제멋대로 맨 위로 가있다던가 하는 경우가 있어 추가해 놓은 코드이다
    if (prevFullscreen && !fullscreen) {
      /**
       *  isAuto값이 true인 경우는 톡 진행 중 풀스크린이 자동으로 뜬 경우이다.
       *  반대의 경우는 톡이 다 지나간 뒤에 직접 유저가 비디오등을 탭 해서 풀스크린을 띄운 경우.
       *  isAuto값이 true일 때는 톡 진행 중 이므로 뷰어 맨 아래로 보내고,
       *  유저가 직접 눌러서 띄운 경우는 이전 톡 탐색중인 걸로 간주해 해당 요소가 뷰포트 가운데 오도록 스크롤을 해 준다.
       */
      const targetTalk = talkElementMap[prevFullscreen.talkId];
      if (!targetTalk) {
        return;
      }
      prevFullscreen.isAuto
        ? scrollToWindowBottommost()
        : verticalWindowScrollIntoView({
            element: targetTalk,
            position: 'middle',
          });
    }
  }, [
    prevFullscreen,
    fullscreen,
    isViewerLoaded,
    isViewerEndExposed,
    fontLoadStatus,
    talkElementMap,
  ]);

  const prevViewingMode = usePrevious(viewingMode);
  // 스크롤 모드일 때 visitedTalkCount가 변경되었는데, 현재 화면에 그 톡이 없는 경우는 그 톡으로 이동시켜줌 (주로 특정 톡 index로 바로 이동했을 때 사용)
  useEffect(() => {
    if (
      viewingMode !== ViewingMode.Scroll ||
      isViewerEndExposed ||
      // 웹폰트 로딩 후에는 텍스트들이 재배치되면서 컴포넌트의 크기가 변할 수 있다. 즉 스크롤이 정확하지 않은 위치로 갈 수 있어서,
      // 웹폰트 로딩 이전에는 스크롤을 작동시키지 않는다.
      fontLoadStatus === FontLoadStatus.BeforeLoad
    ) {
      return;
    }
    // 모드 변환시 톡이 한번 쭉 리렌더되면서 스크롤을 못 찾는 기기가 가끔 있어서 모드전환시에 스크롤을 초기화시켜준다.
    if (prevViewingMode !== viewingMode) {
      window.scrollTo(0, 0);
    }
    const currentTalk = Object.values(talkMap).find(
      talk =>
        talk.productId === currentProductId &&
        talk.talkIndex === visitedTalkCount
    );
    if (!currentTalk) {
      return;
    }
    const talkEl = talkElementMap[currentTalk.id];
    if (!talkEl) {
      return;
    }
    // display none 상태의 톡은 현재 씬에 존재하지 않는 톡으로 취급해서 해당 톡으로 이동하지 않는다.
    const talkElStyle = window.getComputedStyle(talkEl);
    if (talkElStyle.display === 'none') {
      return;
    }

    const rect = talkEl.getBoundingClientRect();
    const windowInnerHeight = window.innerHeight;

    // 대상 톡이 화면 밖에있다면 해당 톡이 맨 아래오도록 스크롤 조정
    const isTalkPositionedOutsideViewport =
      (rect.top <= 0 && rect.bottom <= 0) ||
      (rect.top >= windowInnerHeight && rect.bottom >= windowInnerHeight);
    if (isTalkPositionedOutsideViewport) {
      verticalWindowScrollIntoView({
        element: talkEl,
        position: 'bottom',
        margin: BOTTOM_SCROLL_MARGIN,
      });
      return;
    }
    let nextTalk = Object.values(talkMap).find(
      talk =>
        talk.productId === currentProductId &&
        talk.talkIndex === visitedTalkCount + 1
    );
    if (nextTalk && talkMap[nextTalk.id].datatype === TalkDataType.Bgm) {
      nextTalk = Object.values(talkMap).find(
        talk =>
          talk.productId === currentProductId &&
          talk.talkIndex === visitedTalkCount + 2
      );
    }
    // 마지막 톡이 없으면 현재의 visitedTalkCount가 마지막 톡을 가르키므로 별도의 동작이 더 필요 없다.
    if (!nextTalk) {
      return;
    }
    const nextTalkEl = talkElementMap[nextTalk.id];
    const nextTalkRect = nextTalkEl.getBoundingClientRect();
    // 다음 톡이 뷰포트 내 존재하는 경우는 스크롤을 재조정해준다
    if (
      nextTalkRect.top > 0 &&
      nextTalkRect.bottom < windowInnerHeight &&
      talkMap[nextTalk.id].datatype !== TalkDataType.None
    ) {
      verticalWindowScrollIntoView({
        element: talkEl,
        position: 'bottom',
        margin: BOTTOM_SCROLL_MARGIN,
      });
      // 씬 최상단 같은 경우 다음 톡이 뷰포트 내 존재해도 스크롤이 불가능한 경우가 있다. 그래서 스크롤 이벤트에 달려있는 calculateAndDispatchExposedArea를 여기서 한번 더 실행해준다.
      calculateAndDispatchExposedArea();
    }
  }, [
    visitedTalkCount,
    talkMap,
    viewingMode,
    prevViewingMode,
    calculateAndDispatchExposedArea,
    isViewerEndExposed,
    currentProductId,
    fontLoadStatus,
    talkElementMap,
  ]);
  return null;
}

const BOTTOM_SCROLL_MARGIN = 5;
